import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePremium } from '../contexts/PremiumContext';
import { useTranslation } from '../hooks/useTranslation';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where, orderBy, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FRIENDSHIP_TYPES, getFriendshipTypeName, getRelationshipIcon } from '../utils/friendshipTypes';
import ScheduleAppointmentModal from './ScheduleAppointmentModal';
import CadenceTracker from './CadenceTracker';
import ReflectionPrompt from './ReflectionPrompt';
import { getContactStatus } from '../utils/contactStatus';

const FriendProfile = ({ friendId, onBack, onStartJourney, onEditProfile, onShowInteractionHistory, onScheduleAppointment, onShowGoals, onSelectFriendshipType, onFriendChange }) => {
  const { currentUser } = useAuth();
  const { isFriendAccessible } = usePremium();
  const { t } = useTranslation();
  const [friend, setFriend] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [contactHistory, setContactHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: '',
    targetDate: ''
  });
  const [showScheduleAppointment, setShowScheduleAppointment] = useState(false);
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [birthdayValue, setBirthdayValue] = useState('');
  const [isEditingContactFrequency, setIsEditingContactFrequency] = useState(false);
  const [showInteractionHistory, setShowInteractionHistory] = useState(false);
  const [reflections, setReflections] = useState([]);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [showReflectionView, setShowReflectionView] = useState(false);
  const [allFriends, setAllFriends] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [showReflectionPrompt, setShowReflectionPrompt] = useState(false);
  const [reflectionData, setReflectionData] = useState(null);
  const [contactForm, setContactForm] = useState({
    type: 'call',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    notes: '',
    plannedAppointment: false,
    appointmentDate: '',
    appointmentTime: '',
    appointmentTitle: '',
    appointmentLocation: '',
    createFollowUpReminder: false,
    conversationSummary: '',
    followUpQuestion: '',
    followUpDate: '',
    followUpTime: '',
    emailReminder: false
  });

  console.log('FriendProfile component rendered with:', { friendId, currentUser: !!currentUser });

  // Load all friends for the selector
  useEffect(() => {
    const loadAllFriends = async () => {
      if (!currentUser) return;

      try {
        // Try with orderBy first, fallback to without if index is missing
        let friendsQuery;
        try {
          friendsQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid),
            orderBy('name', 'asc')
          );
        } catch (orderByError) {
          // If orderBy fails, try without it
          console.log('orderBy failed, trying without:', orderByError);
          friendsQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid)
          );
        }
        
        const friendsSnapshot = await getDocs(friendsQuery);
        const friendsData = friendsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually if orderBy wasn't used
        if (friendsData.length > 0 && !friendsData[0].name) {
          // If no name field, just use as is
        } else {
          friendsData.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        }
        
        console.log('Loaded friends for selector:', friendsData.length);
        setAllFriends(friendsData);
      } catch (err) {
        console.error('Error loading friends:', err);
        // Try a simpler query as fallback
        try {
          const simpleQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid)
          );
          const simpleSnapshot = await getDocs(simpleQuery);
          const simpleData = simpleSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          simpleData.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
          console.log('Loaded friends (fallback):', simpleData.length);
          setAllFriends(simpleData);
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
          setAllFriends([]);
        }
      }
    };

    loadAllFriends();
  }, [currentUser]);

  useEffect(() => {
    const loadFriendData = async () => {
      if (!friendId || !currentUser) {
        console.log('Missing friendId or currentUser:', { friendId, currentUser: !!currentUser });
        return;
      }

      try {
        setLoading(true);
        console.log('Loading friend data for:', friendId);
        
        // Load friend data
        const friendDoc = await getDoc(doc(db, 'friends', friendId));
        console.log('Friend document exists:', friendDoc.exists());
        
        if (friendDoc.exists()) {
          const friendData = { id: friendDoc.id, ...friendDoc.data() };
          console.log('Friend data loaded:', friendData);
          setFriend(friendData);
          // Initialize birthday value if it exists
          if (friendData.birthday) {
            try {
              // Handle Firestore Timestamp or Date object
              let birthdayDate;
              if (friendData.birthday.toDate) {
                // Firestore Timestamp
                birthdayDate = friendData.birthday.toDate();
              } else if (friendData.birthday instanceof Date) {
                birthdayDate = friendData.birthday;
              } else {
                // String date
                birthdayDate = new Date(friendData.birthday);
              }
              
              if (!isNaN(birthdayDate.getTime())) {
                const year = birthdayDate.getFullYear();
                const month = String(birthdayDate.getMonth() + 1).padStart(2, '0');
                const day = String(birthdayDate.getDate()).padStart(2, '0');
                setBirthdayValue(`${year}-${month}-${day}`);
              } else {
                setBirthdayValue('');
              }
            } catch (error) {
              console.error('Error parsing birthday:', error);
              setBirthdayValue('');
            }
          } else {
            setBirthdayValue('');
          }

          // Load appointments for this friend (without orderBy to avoid index issues)
          try {
            const appointmentsQuery = query(
              collection(db, 'appointments'),
              where('userId', '==', currentUser.uid),
              where('friendId', '==', friendId)
            );
            const appointmentsSnapshot = await getDocs(appointmentsQuery);
            const appointmentsData = appointmentsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log('Appointments loaded:', appointmentsData.length);
            setAppointments(appointmentsData);
          } catch (appointmentErr) {
            console.error('Error loading appointments:', appointmentErr);
            setAppointments([]);
          }

          // Load contact history for this friend (without orderBy to avoid index issues)
          try {
            const contactQuery = query(
              collection(db, 'contactHistory'),
              where('userId', '==', currentUser.uid),
              where('friendId', '==', friendId)
            );
            const contactSnapshot = await getDocs(contactQuery);
            const contactData = contactSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log('Contact history loaded:', contactData.length);
            setContactHistory(contactData);
          } catch (contactErr) {
            console.error('Error loading contact history:', contactErr);
            setContactHistory([]);
          }

          // Load reflections for this friend
          try {
            const reflectionsQuery = query(
              collection(db, 'reflections'),
              where('userId', '==', currentUser.uid)
            );
            const reflectionsSnapshot = await getDocs(reflectionsQuery);
            const reflectionsData = reflectionsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Filter reflections for this friend
            const friendReflections = reflectionsData.filter(r => 
              String(r.friendId) === String(friendId) || r.friendName === friendData.name
            );
            setReflections(friendReflections);
          } catch (reflectionErr) {
            console.error('Error loading reflections:', reflectionErr);
            setReflections([]);
          }
        } else {
          console.log('Friend document does not exist');
          setError('Friend not found');
        }
      } catch (err) {
        console.error('Error loading friend data:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          friendId,
          currentUserId: currentUser?.uid
        });
        setError(`Failed to load friend data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadFriendData();
  }, [friendId, currentUser]);


  const formatLastInteraction = (lastContactDate) => {
    if (!lastContactDate) return t('never');
    
    const last = new Date(lastContactDate);
    if (isNaN(last.getTime())) return t('never');
    
    const now = new Date();
    const diffTime = now - last;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const remainingDays = diffDays % 7;
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    if (diffDays <= 0) return t('today');
    if (diffDays === 1) return t('oneDayAgo');
    if (diffDays < 7) return t('profileDaysAgo', { count: diffDays });
    
    // Show weeks and days for periods less than a month
    if (diffWeeks < 4) {
      if (remainingDays === 0) {
        return diffWeeks === 1 ? t('oneWeekAgo') : t('profileWeeksAgo', { count: diffWeeks });
      } else {
        const weekText = diffWeeks === 1 ? t('oneWeek') : t('weeks', { weeks: diffWeeks });
        const dayText = remainingDays === 1 ? t('oneDay') : t('days', { days: remainingDays });
        return `${weekText} ${t('and')} ${dayText} ${t('ago')}`;
      }
    }
    
    // For longer periods, show months or years
    if (diffMonths < 12) {
      return diffMonths === 1 ? t('oneMonthAgo') : t('profileMonthsAgo', { count: diffMonths });
    }
    
    return diffYears === 1 ? t('oneYearAgo') : t('profileYearsAgo', { count: diffYears });
  };

  const getInteractionAlert = (lastContactDate) => {
    if (!lastContactDate) return { type: 'urgent', message: t('noRecentInteraction') };
    
    const last = new Date(lastContactDate);
    if (isNaN(last.getTime())) return { type: 'urgent', message: t('noRecentInteraction') };
    
    const now = new Date();
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 7) return { type: 'good', message: t('recentlyConnected') };
    if (diffDays <= 30) return { type: 'warning', message: t('overdueForContact') };
    return { type: 'urgent', message: t('longTimeSinceLastContact') };
  };

  const getNextAppointment = (appointments) => {
    if (!appointments || appointments.length === 0) return null;
    
    const now = new Date();
    const upcomingAppointments = appointments
      .filter(apt => apt.status === 'scheduled' && new Date(apt.date) > now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return upcomingAppointments.length > 0 ? upcomingAppointments[0] : null;
  };

  const getRelationshipIcon = (relationship) => {
    const icons = {
      'friend': '👫',
      'best-friend': '💕',
      'colleague': '👔',
      'family': '👨‍👩‍👧‍👦',
      'acquaintance': '👋',
      'mentor': '🧠',
      'neighbor': '🏠'
    };
    return icons[relationship] || '👫';
  };

  const getFriendshipType = (relationship) => {
    return getFriendshipTypeName(relationship);
  };

  const getFriendshipGoals = (goals) => {
    if (!goals || goals.length === 0) return t('notSetYet');
    
    // If goals is an array of objects, extract the titles
    if (Array.isArray(goals) && goals.length > 0) {
      if (typeof goals[0] === 'object' && goals[0].title) {
        // Goals are objects with title property
        return goals.map(goal => goal.title).join(', ');
      } else if (typeof goals[0] === 'string') {
        // Goals are already strings
        return goals.join(', ');
      }
    }
    
    return t('notSetYet');
  };

  const formatNextAppointment = (appointments) => {
    const nextAppointment = getNextAppointment(appointments);
    if (!nextAppointment) return t('nothingPlannedYet');
    
    const date = new Date(nextAppointment.date);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const getJourneyProgress = (journeys) => {
    if (!journeys || journeys.length === 0) return [];
    
    // Get all active journeys, or if none are active, get all journeys
    const activeJourneys = journeys.filter(j => j.status === 'active');
    const journeysToShow = activeJourneys.length > 0 ? activeJourneys : journeys;
    
    if (journeysToShow.length === 0) return [];
    
    return journeysToShow.map(journey => {
      const totalActivities = Object.values(journey.progress || {}).reduce((sum, week) => sum + (week.total || 0), 0);
      const completedActivities = Object.values(journey.progress || {}).reduce((sum, week) => sum + (week.completed || 0), 0);
      const percentage = totalActivities > 0 ? Math.round((completedActivities / totalActivities) * 100) : 0;
      
      // Get title from journey.title, or format journey.type, or use default
      let journeyTitle = journey.title;
      if (!journeyTitle && journey.type) {
        journeyTitle = journey.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      if (!journeyTitle) {
        journeyTitle = t('friendshipJourney');
      }
      
      return {
        id: journey.id,
        title: journeyTitle,
        progress: percentage,
        completed: completedActivities,
        total: totalActivities,
        type: journey.type
      };
    });
  };

  const addGoal = async (e) => {
    e.preventDefault();
    if (!friend || !newGoal.title.trim() || !newGoal.category) return;

    try {
      const friendRef = doc(db, 'friends', friend.id);
      const goalId = `goal-${Date.now()}`;
      const goalData = {
        id: goalId,
        title: newGoal.title,
        description: newGoal.description,
        category: newGoal.category,
        targetDate: newGoal.targetDate,
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      const existingGoals = friend.goals || [];
      await updateDoc(friendRef, { 
        goals: [...existingGoals, goalData]
      });
      
      // Update local state
      setFriend({ ...friend, goals: [...existingGoals, goalData] });
      
      setNewGoal({
        title: '',
        description: '',
        category: '',
        targetDate: ''
      });
      setShowGoalsModal(false);
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };

  const getGoalCategoryLabel = (category) => {
    const categoryMap = {
      'connection': t('goalCategoryConnection'),
      'communication': t('goalCategoryCommunication'),
      'fun': t('goalCategoryFun'),
      'support': t('goalCategorySupport'),
      'growth': t('goalCategoryGrowth'),
      'other': t('goalCategoryOther')
    };
    return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const deleteGoal = async (goalId) => {
    if (window.confirm(t('confirmDeleteGoal'))) {
      try {
        const friendRef = doc(db, 'friends', friend.id);
        const updatedGoals = friend.goals.filter(goal => goal.id !== goalId);
        
        await updateDoc(friendRef, { goals: updatedGoals });
        setFriend({ ...friend, goals: updatedGoals });
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const handleAppointmentAdded = (newAppointment) => {
    setAppointments([...appointments, newAppointment]);
  };

  const handleAppointmentDeleted = (appointmentId) => {
    setAppointments(appointments.filter(apt => apt.id !== appointmentId));
  };

  const formatBirthday = (birthday) => {
    if (!birthday) return t('notSet');
    
    try {
      let birthdayDate;
      // Handle Firestore Timestamp or Date object
      if (birthday.toDate) {
        birthdayDate = birthday.toDate();
      } else if (birthday instanceof Date) {
        birthdayDate = birthday;
      } else {
        birthdayDate = new Date(birthday);
      }
      
      if (isNaN(birthdayDate.getTime())) return t('notSet');
      
      return birthdayDate.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return t('notSet');
    }
  };

  const saveBirthday = async () => {
    if (!friend) return;
    
    try {
      const friendRef = doc(db, 'friends', friend.id);
      const updateData = {};
      
      if (birthdayValue) {
        // Save as ISO string
        updateData.birthday = new Date(birthdayValue).toISOString();
      } else {
        // Remove birthday if cleared
        updateData.birthday = null;
      }
      
      await updateDoc(friendRef, updateData);
      
      // Update local state
      setFriend({ 
        ...friend, 
        birthday: birthdayValue ? new Date(birthdayValue).toISOString() : null 
      });
      
      setIsEditingBirthday(false);
    } catch (error) {
      console.error('Error saving birthday:', error);
    }
  };

  const handleBirthdayChange = (e) => {
    setBirthdayValue(e.target.value);
  };

  const formatContactFrequency = (frequency) => {
    if (!frequency) return t('notSet');
    const frequencyMap = {
      'daily': t('daily'),
      'weekly': t('weekly'),
      'biweekly': t('biweekly'),
      'monthly': t('monthly'),
      'quarterly': t('quarterly')
    };
    return frequencyMap[frequency] || frequency.replace(/\b\w/g, l => l.toUpperCase());
  };

  const getContactTypeLabel = (type) => {
    const typeMap = {
      'call': t('contactTypeCall'),
      'email': t('contactTypeEmail'),
      'text': t('contactTypeText'),
      'video': t('contactTypeVideo'),
      'meeting': t('contactTypeMeeting'),
      'other': t('contactTypeOther')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };


  const findReflectionForInteraction = (interaction) => {
    if (!interaction || !interaction.id) return null;
    return reflections.find(r => r.contactId === interaction.id);
  };

  const deleteContactRecord = async (contactId) => {
    if (window.confirm(t('deleteContactRecord'))) {
      try {
        await deleteDoc(doc(db, 'contactHistory', contactId));
        setContactHistory(contactHistory.filter(c => c.id !== contactId));
      } catch (error) {
        console.error('Error deleting contact record:', error);
      }
    }
  };

  const reloadContactHistory = async () => {
    if (!friend || !currentUser) return;
    try {
      const contactQuery = query(
        collection(db, 'contactHistory'),
        where('userId', '==', currentUser.uid),
        where('friendId', '==', friend.id)
      );
      const contactSnapshot = await getDocs(contactQuery);
      const contactData = contactSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContactHistory(contactData);
    } catch (error) {
      console.error('Error reloading contact history:', error);
    }
  };

  const addContactRecord = async (friendId, friendName, type, date, duration = '', notes = '') => {
    if (!currentUser) {
      console.error('User not authenticated, cannot save contact record');
      return;
    }

    const contactData = {
      friendId,
      friendName,
      type,
      date: date || new Date().toISOString(),
      duration,
      notes,
      userId: currentUser.uid,
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'contactHistory'), contactData);
    const newRecord = { id: docRef.id, ...contactData };
    setContactHistory((prev) => [newRecord, ...prev]);
  };

  const saveContactForm = async (e) => {
    e.preventDefault();
    if (!friend || isSavingContact) return;

    if (!contactForm.date || !contactForm.time) {
      alert('Please add both date and time before saving.');
      return;
    }
    if (contactForm.plannedAppointment && (!contactForm.appointmentTitle || !contactForm.appointmentDate || !contactForm.appointmentTime)) {
      alert('Please complete all appointment fields before saving.');
      return;
    }
    if (contactForm.createFollowUpReminder && (!contactForm.followUpQuestion || !contactForm.followUpDate)) {
      alert('Please add a follow-up question and reminder date before saving.');
      return;
    }

    let contactSaved = false;
    setIsSavingContact(true);
    try {
      const contactDateTime = new Date(`${contactForm.date}T${contactForm.time}`);
      if (isNaN(contactDateTime.getTime())) {
        alert('The contact date or time is invalid. Please check and try again.');
        return;
      }

      await addContactRecord(
        friend.id,
        friend.name,
        contactForm.type,
        contactDateTime.toISOString(),
        '',
        contactForm.notes
      );
      contactSaved = true;

      if (contactForm.plannedAppointment && contactForm.appointmentTitle && contactForm.appointmentDate) {
        try {
          const appointmentData = {
            title: contactForm.appointmentTitle,
            date: contactForm.appointmentDate,
            time: contactForm.appointmentTime,
            location: contactForm.appointmentLocation,
            notes: `Planned during ${contactForm.type} on ${contactForm.date}`,
            type: 'meeting',
            friendId: friend.id,
            friendName: friend.name,
            userId: currentUser.uid,
            status: 'scheduled',
            createdAt: new Date().toISOString()
          };

          const aptRef = await addDoc(collection(db, 'appointments'), appointmentData);
          setAppointments((prev) => [...prev, { id: aptRef.id, ...appointmentData }]);
        } catch (appointmentError) {
          console.error('Error saving planned appointment:', appointmentError);
        }
      }

      if (contactForm.createFollowUpReminder && contactForm.followUpQuestion && contactForm.followUpDate) {
        try {
          const reminderData = {
            friendId: friend.id,
            friendName: friend.name,
            userId: currentUser.uid,
            sourceContactType: contactForm.type,
            conversationSummary: contactForm.conversationSummary || '',
            followUpQuestion: contactForm.followUpQuestion,
            reminderDate: contactForm.followUpDate,
            reminderTime: contactForm.followUpTime || '09:00',
            sendEmailReminder: !!contactForm.emailReminder,
            emailSent: false,
            status: 'pending',
            createdAt: new Date().toISOString()
          };

          await addDoc(collection(db, 'followUpReminders'), reminderData);
        } catch (reminderError) {
          console.error('Error saving follow-up reminder:', reminderError);
          const reminderErrorCode = reminderError?.code || 'unknown-error';
          const reminderErrorMessage = reminderError?.message || 'No additional details';
          alert(`Your interaction was saved, but the follow-up reminder failed (${reminderErrorCode}). ${reminderErrorMessage}`);
        }
      }

      try {
        const friendRef = doc(db, 'friends', friend.id);
        await updateDoc(friendRef, {
          lastContactDate: contactDateTime.toISOString()
        });

        setFriend((prev) =>
          prev ? { ...prev, lastContactDate: contactDateTime.toISOString() } : prev
        );
      } catch (friendUpdateError) {
        console.error('Error updating friend last contact date:', friendUpdateError);
      }

      if (['call', 'meeting', 'video', 'lunch', 'dinner', 'coffee', 'activity', 'email', 'text'].includes(contactForm.type)) {
        setReflectionData({
          friendId: friend.id,
          friendName: friend.name,
          contactType: contactForm.type
        });
        setShowReflectionPrompt(true);
      }

      setContactForm({
        type: 'call',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        notes: '',
        plannedAppointment: false,
        appointmentDate: '',
        appointmentTime: '',
        appointmentTitle: '',
        appointmentLocation: '',
        createFollowUpReminder: false,
        conversationSummary: '',
        followUpQuestion: '',
        followUpDate: '',
        followUpTime: '',
        emailReminder: false
      });
      setShowContactModal(false);
    } catch (error) {
      console.error('Error saving contact:', error);
      if (!contactSaved) {
        alert('Could not save interaction. Please try again.');
      }
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleSaveReflection = async (reflection) => {
    if (!currentUser) {
      console.error('No current user, cannot save reflection');
      return;
    }

    try {
      const reflectionRecord = {
        ...reflection,
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'reflections'), reflectionRecord);

      const reflectionsQuery = query(
        collection(db, 'reflections'),
        where('userId', '==', currentUser.uid)
      );
      const reflectionsSnapshot = await getDocs(reflectionsQuery);
      const reflectionsData = reflectionsSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      }));
      setReflections(reflectionsData);
    } catch (error) {
      console.error('Error saving reflection:', error);
    }
  };

  const openLogInteractionModal = () => {
    setContactForm({
      type: 'call',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      notes: '',
      plannedAppointment: false,
      appointmentDate: '',
      appointmentTime: '',
      appointmentTitle: '',
      appointmentLocation: '',
      createFollowUpReminder: false,
      conversationSummary: '',
      followUpQuestion: '',
      followUpDate: '',
      followUpTime: '',
      emailReminder: false
    });
    setShowContactModal(true);
  };

  const saveContactFrequency = async (newFrequency) => {
    if (!friend) return;
    
    // Don't do anything if frequency hasn't changed
    if (friend.contactFrequency === newFrequency) {
      setIsEditingContactFrequency(false);
      return;
    }
    
    try {
      const friendRef = doc(db, 'friends', friend.id);
      
      // Calculate current streak before changing frequency
      // We'll need to pass this to CadenceTracker or calculate it here
      // For now, we'll store the frequency change and let CadenceTracker handle the calculation
      
      // Get existing frequency history or create new array
      const frequencyHistory = friend.frequencyHistory || [];
      
      // Add current frequency to history before changing
      const historyEntry = {
        frequency: friend.contactFrequency,
        changedAt: new Date().toISOString(),
        // Streak will be calculated by CadenceTracker when it processes the history
      };
      
      await updateDoc(friendRef, { 
        contactFrequency: newFrequency,
        frequencyHistory: [...frequencyHistory, historyEntry],
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setFriend({ 
        ...friend, 
        contactFrequency: newFrequency,
        frequencyHistory: [...frequencyHistory, historyEntry]
      });
      setIsEditingContactFrequency(false);
    } catch (error) {
      console.error('Error saving contact frequency:', error);
    }
  };

  const toggleCountsAsCloseFriend = async () => {
    if (!friend) return;
    
    const newValue = !friend.countsAsCloseFriend;
    
    try {
      const friendRef = doc(db, 'friends', friend.id);
      await updateDoc(friendRef, {
        countsAsCloseFriend: newValue,
        updatedAt: serverTimestamp()
      });
      
      setFriend({ ...friend, countsAsCloseFriend: newValue });
      
      // Notify parent component if callback exists
      if (onFriendChange) {
        onFriendChange(friend.id);
      }
    } catch (error) {
      console.error('Error updating countsAsCloseFriend:', error);
    }
  };

  if (loading) {
    return (
      <div className="friend-profile">
        <div className="friend-profile-header">
          <button className="back-btn" onClick={onBack}>
            <span className="back-arrow">←</span>
            {t('back')}
          </button>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('loadingFriendProfile')}</p>
        </div>
      </div>
    );
  }

  if (error || !friend) {
    return (
      <div className="friend-profile">
        <div className="friend-profile-header">
          <button className="back-btn" onClick={onBack}>
            <span className="back-arrow">←</span>
            {t('back')}
          </button>
        </div>
        <div className="error-state">
          <p>{error || t('friendNotFound')}</p>
          <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            <p><strong>{t('debugInfo')}</strong></p>
            <p>{t('friendIdLabel')} {friendId || t('notProvided')}</p>
            <p>{t('currentUserLabel')} {currentUser ? t('authenticated') : t('notAuthenticated')}</p>
            <p>{t('userIdLabel')} {currentUser?.uid || t('notAvailable')}</p>
          </div>
        </div>
      </div>
    );
  }

  const isAccessible = isFriendAccessible(friend);
  const interactionAlert = getInteractionAlert(friend?.lastContactDate);
  const journeyProgress = getJourneyProgress(friend?.journeys);
  const nextAppointment = getNextAppointment(appointments);

  const handleFriendSelect = (selectedFriendId) => {
    if (selectedFriendId !== friendId) {
      if (onFriendChange) {
        onFriendChange(selectedFriendId);
      } else if (onEditProfile) {
        // Fallback to onEditProfile if onFriendChange is not provided
        const selectedFriend = allFriends.find(f => f.id === selectedFriendId);
        if (selectedFriend) {
          onEditProfile(selectedFriend);
        }
      }
    }
  };

  return (
    <div className="friend-profile">
      {/* Header */}
      <div className="friend-profile-header">
        <button className="back-btn" onClick={onBack}>
          <span className="back-arrow">←</span>
          {t('back')}
        </button>
      </div>

      {/* Friend Selector Sub-Menu */}
      {allFriends.length > 0 && (
        <div className="friend-selector-menu">
          <div className="friend-selector-scroll">
            {allFriends.map((f) => {
              const isActive = String(f.id) === String(friendId);
              return (
                <button
                  key={f.id}
                  className={`friend-selector-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleFriendSelect(f.id)}
                  title={f.name || 'Unknown Friend'}
                >
                  <div className="friend-selector-avatar">
                    {getRelationshipIcon(f.relationship || 'friend')}
                  </div>
                  <span className="friend-selector-name">{f.name || 'Unknown'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Profile Section */}
      <div className="profile-section">
        <div className="friend-avatar-profile">
          {getRelationshipIcon(friend?.relationship || 'friend')}
        </div>
        <h1 className="friend-name">{friend?.name || t('unknownFriend')}</h1>
      </div>

      {/* Interaction Alert */}
      <div className={`interaction-alert ${interactionAlert.type}`}>
        <div className="interaction-alert-main">
          <div className="alert-icon">
            {interactionAlert.type === 'urgent' ? '⚠️' : 
             interactionAlert.type === 'warning' ? '⏰' : '✅'}
          </div>
          <div className="alert-content">
            <div className="alert-label">{t('lastInteraction')}</div>
            <div className="alert-time">{formatLastInteraction(friend.lastContactDate)}</div>
          </div>
        </div>
        {isAccessible && (
          <button
            type="button"
            className="interaction-alert-log-btn"
            onClick={openLogInteractionModal}
          >
            {t('logInteraction')}
          </button>
        )}
      </div>

      {/* Friendship Overview */}
      <div className="friendship-overview">
        <h2>{t('friendshipOverviewTitle')}</h2>
        <div className="overview-cards">
          <div 
            className="overview-card type-card"
            onClick={() => {
              if (onSelectFriendshipType && friend) {
                onSelectFriendshipType(friend);
              }
            }}
          >
            <div className="card-content">
              <div className="card-label">{t('typeOfFriendship')}</div>
              <div className="card-value">{getFriendshipType(friend?.relationship || 'friend')}</div>
            </div>
            <div className="card-arrow">→</div>
          </div>
          
          <div 
            className="overview-card"
            onClick={() => {
              if (friend) {
                setShowGoalsModal(true);
              }
            }}
          >
            
            <div className="info-icon">🎯</div>
            <div className="card-content">
              <div className="card-label">{t('friendshipGoals')}</div>
              <div className="card-value">{getFriendshipGoals(friend?.goals)}</div>
            </div>
            <div className="card-arrow">→</div>
          </div>
          
          <div 
            className="overview-card"
            onClick={() => {
              if (friend) {
                setShowScheduleAppointment(true);
              }
            }}
          >
            <div className="card-icon">📅</div>
            <div className="card-content">
              <div className="card-label">{t('nextAppointment')}</div>
              <div className="card-value">{formatNextAppointment(appointments)}</div>
            </div>
            <div className="card-arrow">→</div>
          </div>
          
          <div 
            className="overview-card"
            onClick={() => {
              if (friend) {
                setShowInteractionHistory(true);
              }
            }}
          >
            <div className="card-icon">👁️</div>
            <div className="card-content">
              <div className="card-label">{t('interactionHistory')}</div>
              <div className="card-value">{contactHistory.length} {contactHistory.length === 1 ? t('interaction') : t('interactions')}</div>
            </div>
            <div className="card-arrow">→</div>
          </div>
          
          <div className="overview-card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <div className="card-label">{t('data')}</div>
              <div className="card-value">{t('comingSoon')}</div>
            </div>
          </div>

          {/* Contact Frequency Card */}
          <div className="overview-card contact-frequency-card">
            <div className="card-icon">🔄</div>
            <div className="card-content">
              <div className="card-label">{t('contactFrequency')}</div>
              {isEditingContactFrequency ? (
                <div className="frequency-edit">
                  <select
                    value={friend?.contactFrequency || 'weekly'}
                    onChange={(e) => saveContactFrequency(e.target.value)}
                    className="frequency-select"
                    autoFocus
                    onBlur={() => setIsEditingContactFrequency(false)}
                  >
                    <option value="daily">{t('daily')}</option>
                    <option value="weekly">{t('weekly')}</option>
                    <option value="biweekly">{t('biweekly')}</option>
                    <option value="monthly">{t('monthly')}</option>
                    <option value="quarterly">{t('quarterly')}</option>
                  </select>
                </div>
              ) : (
                <div 
                  className="frequency-display"
                  onClick={() => setIsEditingContactFrequency(true)}
                >
                  <div className="card-value">{formatContactFrequency(friend?.contactFrequency)}</div>
                  <div className="frequency-edit-hint">{t('tapToEdit')}</div>
                </div>
              )}
            </div>
            {!isEditingContactFrequency && <div className="card-arrow">→</div>}
          </div>

          {/* Counts as Close Friend Card */}
          <div 
            className="overview-card close-friend-card"
            onClick={toggleCountsAsCloseFriend}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-icon">{friend?.countsAsCloseFriend ? '💚' : '🤍'}</div>
            <div className="card-content">
              <div className="card-label">{t('countsAsCloseFriend')}</div>
              <div className="card-value">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={friend?.countsAsCloseFriend || false}
                    onChange={toggleCountsAsCloseFriend}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>{friend?.countsAsCloseFriend ? t('yes') : t('no')}</span>
                </label>
              </div>
              <div className="close-friend-hint">{t('countsAsCloseFriendHint')}</div>
            </div>
          </div>

          {/* Birthday Bar */}
          <div className="birthday-bar overview-card">
            <div className="card-icon">🎂</div>
            <div className="card-content">
              <div className="card-label">{t('birthday')}</div>
              {isEditingBirthday ? (
                <div className="birthday-edit">
                  <input
                    type="date"
                    value={birthdayValue}
                    onChange={handleBirthdayChange}
                    className="birthday-input"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <div className="birthday-actions">
                    <button 
                      className="birthday-save-btn"
                      onClick={saveBirthday}
                    >
                      {t('save')}
                    </button>
                    <button 
                      className="birthday-cancel-btn"
                      onClick={() => {
                        setIsEditingBirthday(false);
                        // Reset to original value
                        if (friend?.birthday) {
                          try {
                            let birthdayDate;
                            if (friend.birthday.toDate) {
                              birthdayDate = friend.birthday.toDate();
                            } else if (friend.birthday instanceof Date) {
                              birthdayDate = friend.birthday;
                            } else {
                              birthdayDate = new Date(friend.birthday);
                            }
                            
                            if (!isNaN(birthdayDate.getTime())) {
                              const year = birthdayDate.getFullYear();
                              const month = String(birthdayDate.getMonth() + 1).padStart(2, '0');
                              const day = String(birthdayDate.getDate()).padStart(2, '0');
                              setBirthdayValue(`${year}-${month}-${day}`);
                            } else {
                              setBirthdayValue('');
                            }
                          } catch (error) {
                            setBirthdayValue('');
                          }
                        } else {
                          setBirthdayValue('');
                        }
                      }}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="birthday-display"
                  onClick={() => setIsEditingBirthday(true)}
                >
                  <div className="card-value">{formatBirthday(friend?.birthday)}</div>
                  <div className="birthday-edit-hint">{t('tapToEdit')}</div>
                </div>
              )}
          </div>
          </div>
        </div>
      </div>

      {/* Friendship Journeys */}
      {journeyProgress && journeyProgress.length > 0 && (
        <div className="friendship-journeys">
          <h2>{t('friendshipJourneys')}</h2>
          <div className="journeys-list">
            {journeyProgress.map((journey) => (
              <div 
                key={journey.id}
                className="journey-card"
                onClick={() => {
                  if (onStartJourney && friend) {
                    onStartJourney(friend.id, journey.id);
                  }
                }}
              >
                <div className="journey-card-header">
                  <div className="journey-icon-wrapper">
                    <div className="journey-icon">🛤️</div>
                  </div>
                  <div className="journey-header-content">
                    <h3 className="journey-title">{journey.title}</h3>
                    <div className="journey-stats">
                      <span className="journey-stat-item">
                        <span className="stat-value">{journey.completed}</span>
                        <span className="stat-label">{t('ofActivities', { total: journey.total })}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="journey-progress-section">
                  <div className="journey-progress-header">
                    <span className="progress-label">{t('progress')}</span>
                    <span className="progress-percentage">{journey.progress}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${journey.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Premium Lock Overlay */}
      {!isAccessible && (
        <div className="premium-lock-overlay">
          <div className="lock-content">
            <div className="lock-icon">🔒</div>
            <h3>{t('premiumFeature')}</h3>
            <p>{t('upgradeToAccessProfiles')}</p>
          </div>
        </div>
      )}

      {/* Goals Modal */}
      {showGoalsModal && friend && (
        <div 
          className="add-friend-modal"
          onClick={(e) => {
            if (e.target.classList.contains('add-friend-modal')) {
              setShowGoalsModal(false);
              setNewGoal({
                title: '',
                description: '',
                category: '',
                targetDate: ''
              });
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('goalsFor')} {friend.name}</h3>
            
            {/* Existing Goals */}
            {friend.goals && friend.goals.length > 0 && (
              <div className="existing-goals" style={{ marginBottom: '30px' }}>
                <h4>{t('currentGoals')}</h4>
                {friend.goals.map((goal) => (
                  <div key={goal.id} className="goal-item" style={{ 
                    background: '#f8f9fa', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '15px', 
                    marginBottom: '15px' 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <h5 style={{ margin: '0 0 5px 0', color: '#2D3748' }}>{goal.title}</h5>
                        <span style={{ 
                          fontSize: '0.8rem', 
                          color: '#6B7280', 
                          background: '#f3f4f6', 
                          padding: '2px 8px', 
                          borderRadius: '12px',
                          textTransform: 'capitalize'
                        }}>
                          {getGoalCategoryLabel(goal.category)}
                        </span>
                      </div>
                      <button 
                        onClick={() => deleteGoal(goal.id)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#EF4444', 
                          cursor: 'pointer',
                          fontSize: '1.2rem'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                    {goal.description && (
                      <p style={{ margin: '0 0 10px 0', color: '#6B7280', fontSize: '0.9rem' }}>
                        {goal.description}
                      </p>
                    )}
                    {goal.targetDate && (
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem', color: '#6B7280' }}>
                        {t('targetLabel')} {new Date(goal.targetDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add New Goal Form */}
            <form onSubmit={addGoal}>
              <div className="form-group">
                <label>{t('goalTitleLabel')}</label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  placeholder={t('goalTitlePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('goalDescriptionLabel')}</label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  placeholder={t('goalDescriptionPlaceholder')}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('goalCategoryLabel')}</label>
                  <select
                    value={newGoal.category}
                    onChange={(e) => setNewGoal({...newGoal, category: e.target.value})}
                    required
                  >
                    <option value="">{t('selectCategory')}</option>
                    <option value="connection">{t('goalCategoryConnection')}</option>
                    <option value="communication">{t('goalCategoryCommunication')}</option>
                    <option value="fun">{t('goalCategoryFun')}</option>
                    <option value="support">{t('goalCategorySupport')}</option>
                    <option value="growth">{t('goalCategoryGrowth')}</option>
                    <option value="other">{t('goalCategoryOther')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('targetDateOptional')}</label>
                  <input
                    type="date"
                    value={newGoal.targetDate}
                    onChange={(e) => setNewGoal({...newGoal, targetDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowGoalsModal(false);
                    setNewGoal({
                      title: '',
                      description: '',
                      category: '',
                      targetDate: ''
                    });
                  }}
                >
                  {t('close')}
                </button>
                <button type="submit">{t('addGoal')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={showScheduleAppointment && !!friend}
        onClose={() => setShowScheduleAppointment(false)}
        friend={friend}
        appointments={appointments}
        onAppointmentAdded={handleAppointmentAdded}
        onAppointmentDeleted={handleAppointmentDeleted}
      />

      {/* Log interaction (same flow as friendship tracker) */}
      {showContactModal && friend && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('logInteraction')} {t('with') || 'with'} {friend.name}</h3>
            <form onSubmit={saveContactForm}>
              <div className="form-group">
                <label>{t('contactType')} *</label>
                <select
                  value={contactForm.type}
                  onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
                  required
                >
                  <option value="call">{t('phoneCall')}</option>
                  <option value="email">{t('emailContact')}</option>
                  <option value="text">{t('textMessage')}</option>
                  <option value="video">{t('videoCall')}</option>
                  <option value="meeting">{t('inPersonMeeting')}</option>
                  <option value="other">{t('otherContact')}</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('date')} *</label>
                  <input
                    type="date"
                    value={contactForm.date}
                    onChange={(e) => setContactForm({ ...contactForm, date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('time')} *</label>
                  <input
                    type="time"
                    value={contactForm.time}
                    onChange={(e) => setContactForm({ ...contactForm, time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('notes')}</label>
                <textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                  placeholder={t('notesPlaceholderContact')}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contactForm.createFollowUpReminder}
                    onChange={(e) => setContactForm({ ...contactForm, createFollowUpReminder: e.target.checked })}
                  />
                  <span>Create follow-up reminder</span>
                </label>
              </div>

              {contactForm.createFollowUpReminder && (
                <div className="appointment-planning">
                  <h4>Follow-up reminder</h4>

                  <div className="form-group">
                    <label>Important conversation notes</label>
                    <textarea
                      value={contactForm.conversationSummary}
                      onChange={(e) => setContactForm({ ...contactForm, conversationSummary: e.target.value })}
                      placeholder="What did you talk about that you want to remember?"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Follow-up question *</label>
                    <input
                      type="text"
                      value={contactForm.followUpQuestion}
                      onChange={(e) => setContactForm({ ...contactForm, followUpQuestion: e.target.value })}
                      placeholder="What do you want to ask next time?"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Reminder date *</label>
                      <input
                        type="date"
                        value={contactForm.followUpDate}
                        onChange={(e) => setContactForm({ ...contactForm, followUpDate: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Reminder time</label>
                      <input
                        type="time"
                        value={contactForm.followUpTime}
                        onChange={(e) => setContactForm({ ...contactForm, followUpTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={contactForm.emailReminder}
                        onChange={(e) => setContactForm({ ...contactForm, emailReminder: e.target.checked })}
                      />
                      <span>Also send me an email reminder</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contactForm.plannedAppointment}
                    onChange={(e) => setContactForm({ ...contactForm, plannedAppointment: e.target.checked })}
                  />
                  <span>{t('plannedAppointment')}</span>
                </label>
              </div>

              {contactForm.plannedAppointment && (
                <div className="appointment-planning">
                  <h4>{t('appointmentDetails')}</h4>

                  <div className="form-group">
                    <label>{t('appointmentTitle')} *</label>
                    <input
                      type="text"
                      value={contactForm.appointmentTitle}
                      onChange={(e) => setContactForm({ ...contactForm, appointmentTitle: e.target.value })}
                      placeholder={t('appointmentTitlePlaceholder')}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('appointmentDate')} *</label>
                      <input
                        type="date"
                        value={contactForm.appointmentDate}
                        onChange={(e) => setContactForm({ ...contactForm, appointmentDate: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('appointmentTime')} *</label>
                      <input
                        type="time"
                        value={contactForm.appointmentTime}
                        onChange={(e) => setContactForm({ ...contactForm, appointmentTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={contactForm.appointmentLocation}
                      onChange={(e) => setContactForm({ ...contactForm, appointmentLocation: e.target.value })}
                      placeholder="Where are you meeting?"
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  disabled={isSavingContact}
                  onClick={() => setShowContactModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSavingContact}>
                  {isSavingContact ? 'Saving...' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reflectionData && (
        <ReflectionPrompt
          isOpen={showReflectionPrompt}
          onClose={() => {
            setShowReflectionPrompt(false);
            setReflectionData(null);
          }}
          friendName={reflectionData.friendName}
          contactType={reflectionData.contactType}
          onSaveReflection={handleSaveReflection}
        />
      )}

      {/* Interaction History Modal */}
      {showInteractionHistory && friend && (
        <div className="add-friend-modal">
          <div className="modal-content friend-details-modal">
            <h3>{friend.name} - {t('interactionHistory')}</h3>
            
            <div className="friend-details-header">
              <div className="friend-summary">
                <div className="friend-avatar-large">
                  {getRelationshipIcon(friend.relationship)}
                </div>
                <div className="friend-info-large">
                  <h4>{friend.name}</h4>
                  <p className="relationship-type">
                    {getFriendshipType(friend.relationship)}
                  </p>
                  <div className="contact-status">
                    <span 
                      className="status-indicator"
                      style={{ backgroundColor: getContactStatus(friend).color }}
                    >
                      {getContactStatus(friend).message}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Tracking Section */}
            <div className="progress-tracking-section">
              <CadenceTracker
                friendId={friend.id}
                friendName={friend.name}
                contactHistory={contactHistory}
                targetFrequency={friend.contactFrequency}
                frequencyHistory={friend.frequencyHistory || []}
              />
            </div>

            <div className="interactions-section">
              <div className="interactions-header">
                <h4>{t('allInteractions')}</h4>
                <button 
                  className="btn btn-secondary reload-btn"
                  onClick={reloadContactHistory}
                >
                  {t('reload')}
                </button>
              </div>
              {(() => {
                const friendInteractions = contactHistory.filter(c => {
                  const contactFriendId = String(c.friendId);
                  const selectedFriendId = String(friend.id);
                  return contactFriendId === selectedFriendId;
                });
                const friendAppointments = appointments.filter(a => {
                  const appointmentFriendId = String(a.friendId);
                  const selectedFriendId = String(friend.id);
                  return appointmentFriendId === selectedFriendId;
                });
                
                // Combine and sort all interactions by date
                const allInteractions = [
                  ...friendInteractions.map(interaction => ({
                    ...interaction,
                    type: 'contact',
                    displayType: interaction.type,
                    date: interaction.date,
                    title: `${getContactTypeLabel(interaction.type)} ${t('with')} ${interaction.friendName}`,
                    details: interaction.notes || '',
                    duration: interaction.duration || ''
                  })),
                  ...friendAppointments.map(appointment => ({
                    ...appointment,
                    type: 'appointment',
                    displayType: appointment.type,
                    date: appointment.date,
                    title: appointment.title,
                    details: appointment.notes || '',
                    duration: appointment.time || '',
                    status: appointment.status
                  }))
                ].sort((a, b) => new Date(b.date) - new Date(a.date));

                if (allInteractions.length === 0) {
                  return (
                    <div className="empty-interactions">
                      <div className="empty-icon">📝</div>
                      <p>{t('noInteractionsRecorded')}</p>
                    </div>
                  );
                }

                return (
                  <div className="interactions-list">
                    {allInteractions.map((interaction, index) => {
                      const interactionDate = new Date(interaction.date);
                      const typeIcons = {
                        'call': '📞',
                        'email': '📧',
                        'text': '💬',
                        'meeting': '🤝',
                        'video': '📹',
                        'lunch': '🍽️',
                        'dinner': '🍽️',
                        'coffee': '☕',
                        'activity': '🎯',
                        'other': '💭'
                      };
                      
                      return (
                        <div key={`${interaction.type}-${interaction.id || index}`} className="interaction-item">
                          <div className="interaction-header">
                            <div className="interaction-icon">
                              {typeIcons[interaction.displayType] || '💭'}
                            </div>
                            <div className="interaction-info">
                              <h5>{interaction.title}</h5>
                              <div className="interaction-meta">
                                <span className="interaction-date">
                                  {interactionDate.toLocaleDateString()} {t('at')} {interactionDate.toLocaleTimeString()}
                                </span>
                                {interaction.duration && (
                                  <span className="interaction-duration">
                                    {t('durationLabel')} {interaction.duration}
                                  </span>
                                )}
                                {interaction.status && (
                                  <span className={`interaction-status ${interaction.status}`}>
                                    {interaction.status === 'scheduled' ? t('scheduled') :
                                     interaction.status === 'completed' ? t('completed') : t('cancelled')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {interaction.details && (
                            <div className="interaction-notes">
                              <p>"{interaction.details}"</p>
                            </div>
                          )}
                          
                          {interaction.type === 'contact' && (
                            <div className="interaction-actions">
                              {findReflectionForInteraction(interaction) && (
                                <button 
                                  className="action-btn view-reflection"
                                  onClick={() => {
                                    setSelectedReflection(findReflectionForInteraction(interaction));
                                    setShowReflectionView(true);
                                  }}
                                >
                                  📝 {t('viewReflection')}
                                </button>
                              )}
                              <button 
                                className="action-btn delete"
                                onClick={() => deleteContactRecord(interaction.id)}
                              >
                                🗑️ {t('delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowInteractionHistory(false);
                }}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reflection View Modal */}
      {showReflectionView && selectedReflection && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('reflectionEvaluation')}</h3>
            <div className="reflection-view-content">
              <div className="reflection-section">
                <div className="reflection-label">{t('howWasConversation')}</div>
                <div className="reflection-value">
                  {selectedReflection.mood ? (
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      backgroundColor: selectedReflection.mood === 'excellent' ? '#10B981' :
                                       selectedReflection.mood === 'good' ? '#22C55E' :
                                       selectedReflection.mood === 'okay' ? '#F59E0B' : '#EF4444',
                      color: 'white',
                      fontSize: '0.9rem'
                    }}>
                      {selectedReflection.mood === 'excellent' ? t('moodExcellent') :
                       selectedReflection.mood === 'good' ? t('moodGood') :
                       selectedReflection.mood === 'okay' ? t('moodOkay') : t('moodDifficult')}
                    </span>
                  ) : (
                    <span>{t('notProvided')}</span>
                  )}
                </div>
              </div>

              {selectedReflection.topics && selectedReflection.topics.length > 0 && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('topicsDiscussed')}</div>
                  <div className="reflection-value">
                    {selectedReflection.topics.join(', ')}
                  </div>
                </div>
              )}

              {selectedReflection.highlights && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('highlights')}</div>
                  <div className="reflection-value">{selectedReflection.highlights}</div>
                </div>
              )}

              {selectedReflection.nextSteps && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('nextSteps')}</div>
                  <div className="reflection-value">{selectedReflection.nextSteps}</div>
                </div>
              )}

              {selectedReflection.overall && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('overallReflection')}</div>
                  <div className="reflection-value">{selectedReflection.overall}</div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowReflectionView(false);
                  setSelectedReflection(null);
                }}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendProfile;
